import './SnapshotChart.scss';
import cn from 'classnames';
import { Dispatch, ReactNode, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  ErrorBar,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import { getColorMap, Brand100 } from '../../utils/colors';
import { downloadPNG, exportToExcel } from '../../utils/download-utils';
import { ChartContainer } from '../ChartContainer';
import { Placement, StretchMode, StretchModeType } from '../designConstants';
import { DownloadDropdown } from '../DownloadDropdown';
import { Dropdown } from '../Dropdown';
import {
  DropdownOption,
  DropdownToggleType,
  DropdownToggleTypeValue
} from '../Dropdown/dropdown-constants';
import { InfoTooltip } from '../InfoTooltip';
import { InteractiveDiv } from '../InteractiveEl';
import { ToggleSwitch } from '../ToggleSwitch';
import {
  ActiveTooltip,
  BAR_CATEGORY_GAP,
  DataItem,
  DEFAULT_BAR_CHART_CONTAINER_HEIGHT,
  DEFAULT_FORMATTER,
  SecondaryAxisConfig,
  TOOLTIP_MARGINS,
  TooltipItems
} from './snapshot-chart-constants';
import { SnapshotChartLegend } from './SnapshotChartLegend';
import { SecondaryAxisOverlay } from './SnapshotChartSecondaryAxis';
import { SnapshotChartTooltipContent } from './SnapshotChartTooltip';

const SECONDARY_AXIS_ID = 'snapshot-secondary';

interface SnapshotChartProps {
  colorOverrides?: Record<string, string>;
  data: DataItem[];
  doubleBarChart?: boolean;
  dropdownOptions?: readonly DropdownOption[];
  dropdownValue?: string;
  dropdownToggleType?: DropdownToggleTypeValue;
  // Width behavior of the sort dropdown. Defaults to FILL to preserve existing consumers
  // (incl. GeoMMM); rMMM Model Results passes FIT so the dropdown hugs its content.
  dropdownStretch?: StretchModeType;
  header?: string;
  setDropdownValue?: (value: string) => void;
  setToggleLabel?: Dispatch<SetStateAction<boolean>>;
  title: string;
  tooltipItems?: TooltipItems;
  toggleLabel?: boolean;
  toggleSwitchLabel?: string;
  valueFormatter?: (value: number) => string;
  additionalRightActions?: ReactNode;
  exportFormattedData?: Record<string, unknown>[];
  showDownload?: boolean;
  barChartContainerHeight?: number;
  customXAxisDomain?: [string | number, string | number];
  pngDownloadClassName?: string;
  isWaterfallChart?: boolean;
  showConfidenceIntervals?: boolean;
  enableTooltip?: boolean;
  embedded?: boolean;
  secondaryAxis?: SecondaryAxisConfig;
  onItemClick?: (name: string) => void;
  hideBarLabels?: boolean;
}

export const SnapshotChart = ({
  colorOverrides,
  data,
  doubleBarChart,
  dropdownOptions,
  dropdownValue,
  dropdownToggleType = DropdownToggleType.TEXT_INPUT,
  dropdownStretch = StretchMode.FIT,
  header,
  setDropdownValue,
  setToggleLabel,
  title,
  tooltipItems,
  toggleLabel = false,
  toggleSwitchLabel,
  valueFormatter = DEFAULT_FORMATTER,
  additionalRightActions,
  exportFormattedData,
  showDownload = true,
  barChartContainerHeight = DEFAULT_BAR_CHART_CONTAINER_HEIGHT,
  customXAxisDomain = [0, 'dataMax'],
  pngDownloadClassName,
  isWaterfallChart = false,
  showConfidenceIntervals = false,
  enableTooltip = false,
  embedded = false,
  secondaryAxis,
  onItemClick,
  hideBarLabels = false
}: SnapshotChartProps) => {
  const barContainerRef = useRef<HTMLDivElement>(null);
  const [activeTooltip, setActiveTooltip] = useState<ActiveTooltip | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const excelDownload = useCallback(() => {
    const dataToExport = exportFormattedData || data;
    const resolvedTitle = title ? title.slice(0, 30) : 'snapshot';
    exportToExcel(dataToExport, resolvedTitle.replace(/ /g, '_'));
  }, [title, data, exportFormattedData]);

  const pngDownload = useCallback(async () => {
    await downloadPNG(pngDownloadClassName || '.snapshot-graph-contents', title);
  }, [title, pngDownloadClassName]);

  const colorMap = useMemo(() => {
    const colorsOriginal = getColorMap(data);
    return { ...colorsOriginal, ...colorOverrides };
  }, [colorOverrides, data]);

  const secondaryAxisMax = useMemo(() => {
    if (!secondaryAxis) return 0;
    const finite = Object.values(secondaryAxis.values).filter(
      (v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0
    );
    if (finite.length === 0) return 1;
    return Math.max(...finite) * 1.15;
  }, [secondaryAxis]);

  const handleBarMouseEnter = useCallback(
    (rawData: unknown, index: number) => {
      if (typeof rawData !== 'object' || rawData === null) return;
      const barData = rawData as DataItem;
      const rect = barContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const maxValue = Math.max(...data.map(d => d.value));
      const innerW = rect.width - TOOLTIP_MARGINS.left - TOOLTIP_MARGINS.right;
      const xSvg = (barData.value / maxValue) * innerW + TOOLTIP_MARGINS.left;
      const rowH = (rect.height - (data.length - 1) * BAR_CATEGORY_GAP) / data.length;
      const ySvg = index * (rowH + BAR_CATEGORY_GAP) + rowH / 2;

      const xPage = rect.left + window.scrollX + xSvg;
      const yPage = rect.top + window.scrollY + ySvg;

      setActiveTooltip({
        item: barData,
        style: {
          position: 'absolute',
          pointerEvents: 'none',
          zIndex: 9999,
          left: Math.min(xPage, window.innerWidth - 210),
          top: Math.max(10, yPage - 110)
        }
      });
    },
    [data]
  );

  const handleBarMouseLeave = useCallback(() => {
    setActiveTooltip(null);
  }, []);

  const renderCustomizedLabel = useCallback(
    (rawProps: unknown) => {
      if (typeof rawProps !== 'object' || rawProps === null) return null;
      const {
        x: rawX,
        y: rawY,
        width: rawWidth,
        value
      } = rawProps as {
        x?: unknown;
        y?: unknown;
        width?: unknown;
        value?: string | { top: string; bottom: string };
      };
      const x = Number(rawX ?? 0);
      const y = Number(rawY ?? 0);
      const newWidth = Number.isNaN(Number(rawWidth)) ? 0 : Number(rawWidth ?? 0);
      const splitValue = value?.toString().split(' ');
      if (!value || !splitValue) {
        return null;
      }
      let val1: string;
      let val2: string;
      if (typeof value === 'string') {
        val1 = `${splitValue[0]}${splitValue[1] === 'x' ? ' x' : ''}`;
        val2 =
          splitValue[1] === 'x' ? splitValue.slice(2).join(' ') : splitValue.slice(1).join(' ');
      } else {
        val1 = value.top;
        val2 = value.bottom;
      }
      const showSecondLabel = !doubleBarChart || toggleLabel;

      return (
        <g>
          <text
            className='snapshot-bar-label'
            x={x + newWidth + 4}
            y={showSecondLabel ? y + 5 : y + 11}
            fill={Brand100}
            stroke={Brand100}
            strokeWidth={0}
            textAnchor='start'
            fontWeight={400}
            width={20}
            dominantBaseline='middle'
          >
            {val1}
          </text>
          {showSecondLabel && (
            <text
              className='snapshot-bar-label'
              x={x + newWidth + 4}
              y={y + 17}
              fill={Brand100}
              stroke={Brand100}
              strokeWidth={0}
              textAnchor='start'
              fontWeight={400}
              width={20}
              dominantBaseline='middle'
            >
              {val2}
            </text>
          )}
        </g>
      );
    },
    [toggleLabel, doubleBarChart]
  );

  const graphContents = (
    <div id='snapshot-graph-contents' className='snapshot-graph-contents'>
      {header && !tooltipItems && <div className='snapshot-graph-header'>{header}</div>}
      {header && tooltipItems && (
        <div className='snapshot-graph-header-with-tooltip'>
          <div>{header}</div>
          <div className='snapshot-graph-tooltip-before-text'>
            {tooltipItems.beforeTooltipText}
            <InfoTooltip iconColor={tooltipItems.color}>{tooltipItems.tooltipText}</InfoTooltip>
          </div>
        </div>
      )}
      <div
        className='bar-charts-container'
        style={{ height: data.length * barChartContainerHeight }}
      >
        <div className='left-of-chart'>
          {data.map(item => (
            <SnapshotChartLegend
              name={item.name}
              value={item.value}
              value2={item.value2}
              computedValue={item.computedValue}
              formatter={valueFormatter}
              key={item.name}
              incrementality={item.incrementality}
              icon={item.icon}
              label={hideBarLabels && typeof item.label === 'string' ? item.label : undefined}
              label2={hideBarLabels && typeof item.label2 === 'string' ? item.label2 : undefined}
              onClick={onItemClick ? () => onItemClick(item.name) : undefined}
            />
          ))}
        </div>
        <div
          className='snapshot-bar-container'
          ref={barContainerRef}
          style={{ height: data.length * barChartContainerHeight }}
        >
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              barCategoryGap={BAR_CATEGORY_GAP}
              data={data}
              layout='vertical'
              margin={{ top: 0, right: 50, bottom: 0, left: 0 }}
            >
              <XAxis type='number' hide domain={customXAxisDomain} />
              {secondaryAxis && (
                <XAxis
                  xAxisId={SECONDARY_AXIS_ID}
                  type='number'
                  hide
                  orientation='top'
                  domain={[0, secondaryAxisMax]}
                  allowDataOverflow
                />
              )}
              <YAxis type='category' dataKey='name' hide />
              {isWaterfallChart && (
                <Bar
                  dataKey='waterfallValue'
                  fillOpacity={0}
                  fill='white'
                  isAnimationActive={false}
                  stackId='spend'
                >
                  {data.map(item => (
                    <Cell key={`${item.name}waterfall`} />
                  ))}
                </Bar>
              )}
              <Bar
                dataKey='value'
                isAnimationActive={false}
                opacity={doubleBarChart ? 0.5 : 1}
                stackId={isWaterfallChart ? 'spend' : undefined}
                strokeWidth={2}
                radius={title === 'Spend and Effect Share' ? 0 : 2}
                strokeOpacity={1}
                onMouseEnter={enableTooltip ? handleBarMouseEnter : undefined}
                onMouseLeave={enableTooltip ? handleBarMouseLeave : undefined}
              >
                {data.map(item => (
                  <Cell key={`${item.name}value1`} fill={colorMap[item.name]} />
                ))}
                {showConfidenceIntervals && (
                  <ErrorBar dataKey='errorX' strokeWidth={2} stroke={Brand100} />
                )}
                {(toggleLabel || doubleBarChart) && !hideBarLabels && (
                  <LabelList dataKey='label' content={renderCustomizedLabel} />
                )}
              </Bar>
              {doubleBarChart && (
                <Bar
                  dataKey='value2'
                  isAnimationActive={false}
                  // Only stack value2 in waterfall mode. In plain double-bar
                  // mode we want both bars unstacked so they render side-by-side
                  // in JSX order (value on top, value2 below) — matching the
                  // legend column on the left, where value is listed first.
                  stackId={isWaterfallChart ? 'revenue' : undefined}
                >
                  {data.map(item => (
                    <Cell key={`${item.name}value2`} fill={colorMap[item.name]} />
                  ))}
                  {(toggleLabel || doubleBarChart) && !hideBarLabels && (
                    <LabelList dataKey='label2' content={renderCustomizedLabel} />
                  )}
                </Bar>
              )}
              {secondaryAxis && (
                <SecondaryAxisOverlay
                  data={data}
                  secondaryAxis={secondaryAxis}
                  axisId={SECONDARY_AXIS_ID}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {onItemClick && (
          <>
            <div className='snapshot-row-tints' aria-hidden='true'>
              {data.map((item, idx) => (
                <div
                  key={item.name}
                  className={cn('snapshot-row-tint', {
                    'snapshot-row-tint-hovered': hoveredRow === item.name
                  })}
                  style={{
                    top: idx * barChartContainerHeight,
                    height: barChartContainerHeight
                  }}
                />
              ))}
            </div>
            <div className='snapshot-row-hits' aria-hidden='true'>
              {data.map((item, idx) => (
                <InteractiveDiv
                  key={item.name}
                  className='snapshot-row-hit'
                  style={{
                    top: idx * barChartContainerHeight,
                    height: barChartContainerHeight
                  }}
                  onMouseEnter={() => setHoveredRow(item.name)}
                  onMouseLeave={() => setHoveredRow(prev => (prev === item.name ? null : prev))}
                  onClick={() => onItemClick(item.name)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  const tooltipOverlay = enableTooltip && activeTooltip && (
    <SnapshotChartTooltipContent
      item={activeTooltip.item}
      style={activeTooltip.style}
      colorMap={colorMap}
      valueFormatter={valueFormatter}
    />
  );

  if (embedded) {
    return (
      <div className='snapshot-chart-container snapshot-chart-container-embedded'>
        {graphContents}
        {tooltipOverlay}
      </div>
    );
  }

  return (
    <div className='snapshot-chart-container'>
      <ChartContainer
        title={title}
        rightActions={
          <>
            {additionalRightActions}
            {toggleLabel != null && setToggleLabel && toggleSwitchLabel && (
              <ToggleSwitch
                label={toggleSwitchLabel}
                checked={toggleLabel}
                onChange={setToggleLabel}
              />
            )}
            {dropdownOptions && dropdownValue && setDropdownValue && (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                type={dropdownToggleType}

                stretch={dropdownStretch}
                value={dropdownValue}
                options={dropdownOptions}
                onChange={setDropdownValue}
              />
            )}
            {showDownload && <DownloadDropdown onClickOptions={[excelDownload, pngDownload]} />}
          </>
        }
      >
        {graphContents}
      </ChartContainer>
      {tooltipOverlay}
    </div>
  );
};
